import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faEye, faEyeSlash, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";

import GoogleSignInButton from "../../components/auth/GoogleSignInButton";
import AuthCardFooter from "../../components/auth/AuthCardFooter";
import styles from "./AuthPage.module.css";

import {
  getLocationList,
  getOrganizationList,
} from "../../services/admin/organization";
import {
  loginWithEmail,
  loginWithGoogle,
  applyAuthSuccess,
} from "../../services/auth";
import { handleApiError } from "../../utils/handleApiError";
import { mapGoogleSignInError, GSI_ERROR } from "../../utils/googleSignInErrors";
import useRoles from "../../hooks/useRoles";

const schema = z.object({
  identifier: z
    .string()
    .min(1, "Email or phone number is required")
    .trim(),
  password: z.string().min(1, "Password is required"),
  rememberMe: z.boolean().optional(),
});

const LoginPage = () => {
  const { roles } = useRoles();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const loggedUser = JSON.parse(localStorage.getItem("user"));

  const { refetch: refetchLocation } = useQuery(
    ["locations-list"],
    () => getLocationList(roles),
    { enabled: false }
  );
  const { refetch: refetchOrgs } = useQuery(
    ["organizations-list"],
    () => getOrganizationList(),
    { enabled: false }
  );

  useEffect(() => {
    if (
      token &&
      loggedUser?.external_role?.name === "TOCA Inventory Manager" &&
      !loggedUser?.account_roles?.some((_role) => _role.name === "Super User")
    ) {
      navigate("/inventory/dashboard");
    } else if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate, loggedUser]);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Inline error banner shown directly below the Google button. Null
  // means no banner. Set by handleGoogleCredential and the GSI onError
  // callback; cleared when the user starts typing in the email field
  // or clicks the "Sign in with email" CTA.
  const [googleError, setGoogleError] = useState(null);
  const submittingRef = useRef(false);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      identifier: "",
      password: "",
      rememberMe: localStorage.getItem("remember_me") === "true",
    },
  });

  // react-hook-form's register() accepts onChange/onBlur hooks alongside
  // its internal handlers; we use that to clear the Google banner when
  // the user pivots to the email/password path.
  const identifierRegister = register("identifier", {
    onChange: () => {
      if (googleError) setGoogleError(null);
    },
  });

  // Post-login follow-ups that depend on React Query hooks (refetchOrgs /
  // refetchLocation). Keep this co-located with the component since the
  // hooks can't move into the auth service module.
  const runRoleSideEffects = async (user) => {
    if (user?.external_role?.name === "TOCA Admin") {
      await refetchOrgs();
    }

    if (
      user?.external_role?.name !== "TOCA Admin" &&
      !user?.account_roles?.some((role) => role.name === "Patient") &&
      !user?.account_roles?.some((role) => role.name === "Case Manager")
    ) {
      await refetchLocation();
    }
  };

  const onSubmit = async (data) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);

    // Persist the "Keep me signed in" preference. Long-lived session
    // support is a separate backend ticket; the value is recorded here so
    // we can act on it later without another UI change.
    localStorage.setItem("remember_me", data.rememberMe ? "true" : "false");

    try {
      const response = await loginWithEmail(data.identifier, data.password);
      if (response.data) {
        const user = applyAuthSuccess(response.data);
        await runRoleSideEffects(user);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    if (googleLoading) return;
    setGoogleLoading(true);
    setGoogleError(null);
    try {
      const response = await loginWithGoogle(credential);
      if (response.data) {
        const user = applyAuthSuccess(response.data);
        await runRoleSideEffects(user);
      }
    } catch (error) {
      // All Google sign-in errors render inline per SCRUM-61. The
      // mapping util returns null for user-cancel cases where nothing
      // should be shown.
      const banner = mapGoogleSignInError(error);
      if (banner) setGoogleError(banner);
    } finally {
      setGoogleLoading(false);
    }
  };

  // GoogleLogin's onError fires for popup-blocked / user-cancelled /
  // FedCM-rejected. The SDK gives no discriminator, so we route the
  // signal through the same mapper which decides what (if anything)
  // to show. See utils/googleSignInErrors.js for the heuristic.
  const handleGoogleSignInError = () => {
    const banner = mapGoogleSignInError(GSI_ERROR);
    if (banner) setGoogleError(banner);
  };

  const handleSignInWithEmail = () => {
    setGoogleError(null);
    // react-hook-form's setFocus lands the cursor on the email input
    // already wired via register("identifier").
    setFocus("identifier");
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
        noValidate
        autoComplete="off"
      >
        <div className={styles.field}>
          <label htmlFor="login-identifier" className={styles.label}>
            Email or Phone Number
          </label>
          <div className={styles.inputWrap}>
            <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
            <input
              id="login-identifier"
              type="text"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              placeholder="you@clinic.org"
              className={styles.input}
              aria-invalid={!!errors.identifier}
              {...identifierRegister}
            />
          </div>
          {errors.identifier && (
            <span className={styles.errorMsg}>{errors.identifier.message}</span>
          )}
        </div>

        <div className={styles.field}>
          <div className={styles.labelRow}>
            <label htmlFor="login-password" className={styles.label}>
              Password
            </label>
            <Link to="/recover-password" className={styles.inlineLink}>
              Forgot password?
            </Link>
          </div>
          <div className={styles.inputWrap}>
            <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              className={styles.input}
              aria-invalid={!!errors.password}
              {...register("password")}
            />
            <button
              type="button"
              className={styles.eyeBtn}
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          {errors.password && (
            <span className={styles.errorMsg}>{errors.password.message}</span>
          )}
        </div>

        <label className={styles.checkboxRow}>
          <input type="checkbox" {...register("rememberMe")} />
          <span>Keep me signed in</span>
        </label>

        <button
          type="submit"
          className={styles.primaryBtn}
          disabled={loading || googleLoading}
          aria-busy={loading}
        >
          {loading ? "Signing in..." : (
            <>
              Sign in <FontAwesomeIcon icon={faArrowRight} />
            </>
          )}
        </button>
      </form>

      <div className={styles.divider}>
        <span>OR CONTINUE WITH</span>
      </div>

      <GoogleSignInButton
        onCredential={handleGoogleCredential}
        onError={handleGoogleSignInError}
        loading={googleLoading}
        disabled={loading}
      />

      {googleError && (
        <div
          className={styles.signinErrorBanner}
          role="alert"
          aria-live="polite"
        >
          <span className={styles.signinErrorMessage}>{googleError.message}</span>
          {googleError.showRetryWithEmailCta && (
            <button
              type="button"
              className={styles.signinErrorCta}
              onClick={handleSignInWithEmail}
            >
              Sign in with email
            </button>
          )}
        </div>
      )}

      <p className={styles.requestAccess}>
        Don't have an account?{" "}
        <a
          href="mailto:sales@tocahealth.com?subject=Access%20request"
          className={styles.requestLink}
        >
          Request access
        </a>
      </p>

      <AuthCardFooter />
    </>
  );
};

export default LoginPage;
